import { useCallback, useEffect, useState } from "react";

import { trpc } from "../client";
import { AUTH_TOKEN_EVENT, getToken } from "../../auth/utils";

export type UserProfile = {
  firstName?: string;
  lastName?: string;
};

function toRouteNumberId(id: string): number {
  const parsed = Number(id);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    return 0;
  }

  return parsed;
}

function toOptionalNumberId(id: string | number | null | undefined) {
  if (typeof id === "number") {
    return id;
  }

  if (typeof id === "string") {
    const parsed = toRouteNumberId(id);
    if (parsed > 0) {
      return parsed;
    }
  }
}

export const useCurrentUser = () => {
  const [token, setTokenState] = useState<string | null>(() => getToken());

  useEffect(() => {
    const handleTokenChange = () => {
      setTokenState(getToken());
    };

    window.addEventListener(AUTH_TOKEN_EVENT, handleTokenChange);
    window.addEventListener("storage", handleTokenChange);

    return () => {
      window.removeEventListener(AUTH_TOKEN_EVENT, handleTokenChange);
      window.removeEventListener("storage", handleTokenChange);
    };
  }, []);

  return trpc.authRouter.getCurrentUser.useQuery(undefined, {
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: false,
    refetchOnWindowFocus: true,
    enabled: token != null && token !== "",
  });
};

export const useUser = (id: string) => {
  const userId = toRouteNumberId(id);

  return trpc.usersRouter.findOne.useQuery(
    { id: userId },
    {
      enabled: userId > 0,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
    },
  );
};

export const useLogout = () => {
  const utils = trpc.useUtils();

  return trpc.authRouter.logout.useMutation({
    onSuccess: () => {
      void utils.invalidate();
    },
  });
};

export const useWorkosAuthorizationUrl = () => {
  return trpc.authRouter.getWorkosAuthorizationUrl.useMutation();
};

export const useInvalidateAuthQueries = () => {
  const utils = trpc.useUtils();

  return useCallback(async () => {
    await Promise.all([
      utils.authRouter.getCurrentUser.invalidate(),
      utils.authRouter.getProfile.invalidate(),
    ]);
  }, [utils]);
};

export const usePasswordWorkosSignIn = () => {
  return trpc.authRouter.passwordWorkosSignIn.useMutation();
};

export const useCreateWorkosMagicAuth = () => {
  return trpc.authRouter.createWorkosMagicAuth.useMutation();
};

export const useRequestWorkosPasswordReset = () => {
  return trpc.authRouter.requestWorkosPasswordReset.useMutation();
};

export const useResetWorkosPassword = () => {
  return trpc.authRouter.resetWorkosPassword.useMutation();
};

export const useAuthenticateWorkosMagicAuth = () => {
  return trpc.authRouter.authenticateWorkosMagicAuth.useMutation();
};

export const useAuthenticateWorkosEmailVerification = () => {
  return trpc.authRouter.authenticateWorkosEmailVerification.useMutation();
};

export const useAuthenticateWorkosTotp = () => {
  return trpc.authRouter.authenticateWorkosTotp.useMutation();
};

export const useAuthenticateWorkosOrganizationSelection = () => {
  return trpc.authRouter.authenticateWorkosOrganizationSelection.useMutation();
};

export const useExchangeWorkosCode = () => {
  return trpc.authRouter.exchangeWorkosCode.useMutation({
    retry: false,
  });
};

export const useWorkosUsersManagementWidgetToken = (options?: {
  enabled?: boolean;
}) => {
  return trpc.authRouter.getWorkosUsersManagementWidgetToken.useQuery(
    undefined,
    {
      staleTime: 1000 * 60 * 30,
      gcTime: 1000 * 60 * 60,
      retry: false,
      enabled: options?.enabled ?? true,
    },
  );
};

export const useUpdateProfile = () => {
  const utils = trpc.useUtils();

  return trpc.authRouter.updateProfile.useMutation({
    onSuccess: () => {
      void utils.authRouter.getCurrentUser.invalidate();
      void utils.authRouter.getProfile.invalidate();
    },
  });
};

export const useUpdateUser = () => {
  const utils = trpc.useUtils();

  return trpc.usersRouter.update.useMutation({
    onSuccess: (data) => {
      void utils.usersRouter.findAll.invalidate();
      const userId = toOptionalNumberId(data?.id);
      if (userId !== undefined) {
        void utils.usersRouter.findOne.invalidate({ id: userId });
      }
      void utils.authRouter.getCurrentUser.invalidate();
    },
  });
};
